import { ServerMessage, parseMessage, type ClientMessage } from '@viewer/protocol';
import type { UnlistenFn } from '@tauri-apps/api/event';
import { agentWsSend, onAgentStatus, onAgentWs } from '../tauri/agent';
import type { SignalingStatus } from './client';

/** O que o `SessionController` usa, seja WebSocket direto ou o pipe do agente. */
export interface SignalingTransport {
  connect(): void | Promise<void>;
  close(): void;
  reconnect(): void;
  send(message: ClientMessage): boolean;
}

export interface AgentTransportOptions {
  onStatus(status: SignalingStatus): void;
  onMessage(message: ServerMessage): void;
  onDeviceId(deviceId: string): void;
  log?: (msg: string, ...rest: unknown[]) => void;
}

/**
 * Sinalização pelo agente Windows. O processo SYSTEM é dono do WebSocket;
 * daqui só saem `ClientMessage` e entram as `ServerMessage` que ele repassa.
 */
export class AgentTransport implements SignalingTransport {
  private closed = false;
  private online = false;
  private unlisten: UnlistenFn[] = [];
  private readonly log: (msg: string, ...rest: unknown[]) => void;

  constructor(private readonly options: AgentTransportOptions) {
    this.log = options.log ?? (() => {});
  }

  connect() {
    this.closed = false;
    this.online = false;
    this.options.onStatus('connecting');
    return this.attach();
  }

  close() {
    this.closed = true;
    this.online = false;
    this.detach();
  }

  /** O agente refaz o `hello` quando o token muda; aqui não há socket para reabrir. */
  reconnect() {
    this.log('signaling: reconexão fica a cargo do agente');
  }

  send(message: ClientMessage) {
    if (this.closed || !this.online) {
      this.log('signaling: tentativa de envio sem conexão', message.type);
      return false;
    }
    void agentWsSend(JSON.stringify(message)).catch((err) => {
      this.log('signaling: falha ao enviar pelo agente', err);
    });
    return true;
  }

  private async attach() {
    this.detach();
    const unlisten = await Promise.all([
      onAgentWs((json) => this.onServerJson(json)),
      onAgentStatus((online) => this.onStatus(online)),
    ]);
    if (this.closed) {
      for (const stop of unlisten) stop();
      return;
    }
    this.unlisten = unlisten;
  }

  private onServerJson(json: string) {
    const parsed = parseMessage(ServerMessage, json);
    if (!parsed.ok) {
      this.log('signaling: mensagem inválida do servidor', parsed.error);
      return;
    }
    if (parsed.value.type === 'welcome') {
      this.online = true;
      this.options.onDeviceId(parsed.value.deviceId);
      this.options.onStatus('online');
    }
    this.options.onMessage(parsed.value);
  }

  private onStatus(online: boolean) {
    if (this.closed) return;
    this.online = online;
    this.options.onStatus(online ? 'online' : 'offline');
  }

  private detach() {
    for (const stop of this.unlisten) stop();
    this.unlisten = [];
  }
}
